/**
 * SessionStructureEngine
 *
 * Features:
 *   - Config mode: template picker, block editor with reorder/delete/add, save as custom template
 *   - Live mode: synced timer + auto-advance + inline settings panel for admin
 *   - Custom templates: save/load/delete from Firestore per entity
 */

import React, { useState, useCallback, useRef, useMemo, useEffect, memo } from 'react';
import { collection, doc, setDoc, getDocs, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import {
  ClockIcon,
  PlayIcon,
  PauseIcon,
  AddIcon,
  DeleteIcon,
  CheckIcon,
  ChevronLeft,
  ChevronRight,
} from '../../components/icons';
import { useStructuredGrowth } from './useStructuredGrowth';
import { useSessionFlow } from './useSessionFlow';
import type { StructuredGrowthEntityType, SessionBlock, SessionBlockType } from './types';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BLOCK_DOT_COLORS: Record<SessionBlockType, string> = {
  intro: 'bg-blue-400',
  reflection: 'bg-purple-400',
  hotseat: 'bg-amber-400',
  commitment: 'bg-emerald-400',
  custom: 'bg-gray-300',
};

const BLOCK_CARD_COLORS: Record<SessionBlockType, string> = {
  intro: 'bg-blue-50 border-blue-100 text-blue-700',
  reflection: 'bg-purple-50 border-purple-100 text-purple-700',
  hotseat: 'bg-amber-50 border-amber-100 text-amber-700',
  commitment: 'bg-emerald-50 border-emerald-100 text-emerald-700',
  custom: 'bg-gray-50 border-gray-200 text-gray-700',
};

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60).toString().padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
}

// ---------------------------------------------------------------------------
// Template generator
// ---------------------------------------------------------------------------

export function generateTemplate(memberCount: number, totalMinutes: number = 60): SessionBlock[] {
  const mc = Math.max(1, memberCount);
  const INTRO = 5, PAUSE = 5, CLOSE = 5;
  const available = totalMinutes - INTRO - PAUSE - CLOSE;
  const perMember = Math.max(1, Math.floor(available / mc));
  const half1 = Math.ceil(mc / 2);
  const half2 = mc - half1;
  const ts = Date.now();

  return [
    { id: `t-intro-${ts}`, type: 'intro', label: 'Introductie', durationMinutes: INTRO, order: 0 },
    { id: `t-leden1-${ts}`, type: 'hotseat', label: 'Leden ronde 1', durationMinutes: perMember, order: 1, perPerson: true, memberSlotStart: 0, memberSlotCount: half1 },
    { id: `t-pauze-${ts}`, type: 'custom', label: 'Pauze', durationMinutes: PAUSE, order: 2 },
    { id: `t-leden2-${ts}`, type: 'hotseat', label: 'Leden ronde 2', durationMinutes: perMember, order: 3, perPerson: true, memberSlotStart: half1, memberSlotCount: half2 > 0 ? half2 : 1 },
    { id: `t-close-${ts}`, type: 'commitment', label: 'Afsluiting', durationMinutes: CLOSE, order: 4 },
  ];
}

export const PRESET_TEMPLATES = [
  { label: 'Klein (5)', memberCount: 5 },
  { label: 'Middel (10)', memberCount: 10 },
  { label: 'Groot (30+)', memberCount: 30 },
] as const;

const DEFAULT_BLOCKS: SessionBlock[] = generateTemplate(5);

// ---------------------------------------------------------------------------
// Custom template storage helpers
// ---------------------------------------------------------------------------

const TEMPLATES_COLLECTION = 'sessionTemplates';

export interface CustomTemplate {
  id: string;
  name: string;
  blocks: SessionBlock[];
  createdAt: any;
}

export async function loadCustomTemplates(entityId: string): Promise<CustomTemplate[]> {
  try {
    const snap = await getDocs(collection(db, TEMPLATES_COLLECTION, entityId, 'templates'));
    return snap.docs.map((d) => ({ id: d.id, ...d.data() } as CustomTemplate));
  } catch { return []; }
}

export async function saveCustomTemplate(entityId: string, name: string, blocks: SessionBlock[]): Promise<void> {
  const id = `tpl-${Date.now()}`;
  await setDoc(doc(db, TEMPLATES_COLLECTION, entityId, 'templates', id), {
    name,
    blocks,
    createdAt: serverTimestamp(),
  });
}

export async function deleteCustomTemplate(entityId: string, templateId: string): Promise<void> {
  await deleteDoc(doc(db, TEMPLATES_COLLECTION, entityId, 'templates', templateId));
}

// ---------------------------------------------------------------------------
// Reorder helpers
// ---------------------------------------------------------------------------

function moveBlockUp(blocks: SessionBlock[], idx: number): SessionBlock[] {
  if (idx <= 0) return blocks;
  const arr = [...blocks];
  [arr[idx - 1], arr[idx]] = [arr[idx], arr[idx - 1]];
  return arr.map((b, i) => ({ ...b, order: i }));
}

function moveBlockDown(blocks: SessionBlock[], idx: number): SessionBlock[] {
  if (idx >= blocks.length - 1) return blocks;
  const arr = [...blocks];
  [arr[idx], arr[idx + 1]] = [arr[idx + 1], arr[idx]];
  return arr.map((b, i) => ({ ...b, order: i }));
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

interface SessionStructureEngineProps {
  entityId: string;
  entityType: StructuredGrowthEntityType;
  isAdmin: boolean;
  userId: string;
  liveMode?: boolean;
  meetingId?: string;
  compactLive?: boolean;
  memberCount?: number;
  memberNames?: string[];
  memberAvatars?: (string | null)[];
}

// ---------------------------------------------------------------------------
// Block editor row (shared between inline & config)
// ---------------------------------------------------------------------------

export const BlockEditorRow = memo(function BlockEditorRow({
  block, idx, total, effectiveMembers, dark,
  onLabelChange, onDurationChange, onTogglePP, onMoveUp, onMoveDown, onDelete,
  memberNames, memberAvatars, onAssignedChange,
}: {
  block: SessionBlock; idx: number; total: number; effectiveMembers: number; dark?: boolean;
  onLabelChange: (id: string, v: string) => void;
  onDurationChange: (id: string, v: number) => void;
  onTogglePP: (id: string) => void;
  onMoveUp: (idx: number) => void;
  onMoveDown: (idx: number) => void;
  onDelete: (id: string) => void;
  memberNames?: string[];
  memberAvatars?: (string | null)[];
  onAssignedChange?: (id: string, indices: number[]) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const accent = BLOCK_DOT_COLORS[block.type];
  const assigned = block.assignedMembers ?? [];
  const hasMemberPicker = block.perPerson && memberNames && memberNames.length > 0;

  if (dark) {
    // Compact dark variant (live sidebar)
    return (
      <div className="flex items-center gap-1 px-2 py-1.5 rounded-lg group bg-gray-800/60">
        <div className="flex flex-col shrink-0">
          <button onClick={() => onMoveUp(idx)} disabled={idx === 0}
            className="w-4 h-3 flex items-center justify-center disabled:opacity-20 text-gray-500 hover:text-gray-300">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
          </button>
          <button onClick={() => onMoveDown(idx)} disabled={idx === total - 1}
            className="w-4 h-3 flex items-center justify-center disabled:opacity-20 text-gray-500 hover:text-gray-300">
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
          </button>
        </div>
        <div className={`w-1 h-5 rounded-full shrink-0 ${accent}`} />
        <input type="text" value={block.label}
          onChange={(e) => onLabelChange(block.id, e.target.value)}
          className="flex-1 text-[10px] text-white font-medium bg-transparent border-none focus:outline-none rounded px-1 min-w-0" />
        <button
          onClick={() => onTogglePP(block.id)}
          title={block.perPerson ? 'Ieder lid krijgt eigen tijdslot — klik om uit te zetten' : 'Zet aan om ieder lid een eigen tijdslot te geven'}
          className={`shrink-0 text-[8px] font-bold px-1.5 py-0.5 rounded-full transition-colors ${block.perPerson ? 'bg-blue-500/20 text-blue-300' : 'text-gray-600 hover:text-gray-400'}`}>
          {block.perPerson ? `beurten ×${effectiveMembers}` : 'per lid?'}
        </button>
        <input type="number" value={block.durationMinutes} min={1} max={120}
          onChange={(e) => onDurationChange(block.id, Math.max(1, parseInt(e.target.value, 10) || 1))}
          className="w-8 text-[9px] text-gray-300 text-right bg-gray-700 border-gray-600 border rounded px-1 py-0.5 focus:outline-none" />
        <span className="text-[8px] text-gray-500" title={block.perPerson ? 'minuten per lid' : 'minuten totaal'}>{block.perPerson ? 'min/lid' : 'min'}</span>
        <button onClick={() => onDelete(block.id)}
          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 transition-all shrink-0">
          <DeleteIcon size={10} weight="regular" />
        </button>
      </div>
    );
  }

  // Premium light variant (config panel)
  return (
    <div className="rounded-2xl bg-white border border-gray-100 shadow-sm transition-all hover:shadow-md overflow-hidden">
      {/* Header row */}
      <div className="flex items-center gap-2.5 px-4 py-3 group">
        <div className="flex flex-col shrink-0 -my-1">
          <button onClick={() => onMoveUp(idx)} disabled={idx === 0}
            className="w-5 h-4 flex items-center justify-center disabled:opacity-15 text-gray-300 hover:text-gray-500 transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
          </button>
          <button onClick={() => onMoveDown(idx)} disabled={idx === total - 1}
            className="w-5 h-4 flex items-center justify-center disabled:opacity-15 text-gray-300 hover:text-gray-500 transition-colors">
            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
          </button>
        </div>

        <div className={`w-1 self-stretch rounded-full ${accent}`} />

        <input type="text" value={block.label}
          onChange={(e) => onLabelChange(block.id, e.target.value)}
          className="flex-1 text-[13px] text-gray-900 font-semibold bg-transparent border-none focus:outline-none min-w-0 tracking-tight" />

        <button
          onClick={() => onTogglePP(block.id)}
          title={block.perPerson ? 'Ieder lid krijgt eigen tijdslot — klik om uit te zetten' : 'Zet aan om ieder lid een eigen tijdslot te geven'}
          className={`shrink-0 text-[10px] font-semibold px-2.5 py-0.5 rounded-full transition-colors ${
            block.perPerson
              ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
              : 'bg-gray-100 text-gray-400 hover:bg-gray-200 hover:text-gray-600'
          }`}>
          {block.perPerson
            ? `beurten ×${assigned.length || effectiveMembers}`
            : 'per lid?'}
        </button>

        <input type="number" value={block.durationMinutes} min={1} max={120}
          onChange={(e) => onDurationChange(block.id, Math.max(1, parseInt(e.target.value, 10) || 1))}
          className="w-10 text-xs text-gray-600 text-right bg-gray-50 border border-gray-100 rounded-lg px-2 py-1 focus:outline-none focus:ring-2 focus:ring-gray-200 transition-shadow" />
        <span className="text-[10px] text-gray-400 -ml-1" title={block.perPerson ? 'minuten per lid' : 'minuten totaal'}>{block.perPerson ? 'min/lid' : 'min'}</span>

        <button onClick={() => onDelete(block.id)}
          className="opacity-0 group-hover:opacity-100 w-6 h-6 flex items-center justify-center rounded-full text-gray-300 hover:bg-red-50 hover:text-red-500 transition-all shrink-0">
          <DeleteIcon size={12} weight="regular" />
        </button>

        {hasMemberPicker && (
          <button
            type="button"
            onClick={() => setExpanded((s) => !s)}
            aria-expanded={expanded}
            className={`flex items-center gap-1.5 shrink-0 rounded-xl border-2 px-3 py-2 min-h-[2.25rem] transition-all shadow-sm ${
              expanded
                ? 'border-emerald-300 bg-emerald-50 text-emerald-900 hover:bg-emerald-100'
                : 'border-sky-200 bg-sky-50 text-sky-900 hover:bg-sky-100 hover:border-sky-300'
            }`}>
            <svg className="w-4 h-4 shrink-0 opacity-80" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M18 9v3m0 0V9m0 3h3m-3 0h-3m-2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-[11px] font-bold tracking-tight whitespace-nowrap">
              {expanded ? 'Inklappen' : 'Leden kiezen'}
            </span>
            <svg
              className={`w-4 h-4 shrink-0 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
              fill="none"
              stroke="currentColor"
              strokeWidth={2.5}
              viewBox="0 0 24 24"
              aria-hidden
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
        )}
      </div>

      {/* Hint when collapsed: nog geen leden — maakt “toevoegen” zichtbaar */}
      {hasMemberPicker && !expanded && assigned.length === 0 && (
        <div className="px-4 pb-2 -mt-0.5">
          <p className="text-[11px] text-sky-900/90 bg-sky-50/90 border border-sky-100 rounded-xl px-3 py-2 leading-snug">
            <span className="font-semibold">Leden toevoegen:</span> klik rechts op{' '}
            <span className="font-bold text-sky-800">Leden kiezen</span> en selecteer wie in deze ronde aan de beurt is.
          </p>
        </div>
      )}

      {/* Assigned member avatars (collapsed preview) */}
      {hasMemberPicker && !expanded && assigned.length > 0 && (
        <div className="px-4 pb-3 -mt-1 flex items-center gap-1">
          <div className="flex -space-x-1.5">
            {assigned.slice(0, 8).map((mi) => {
              const av = memberAvatars?.[mi];
              const nm = memberNames![mi] ?? '?';
              return av ? (
                <img key={mi} src={av} alt={nm} title={nm}
                  className="w-6 h-6 rounded-full object-cover ring-2 ring-white" />
              ) : (
                <div key={mi} title={nm}
                  className="w-6 h-6 rounded-full bg-gray-100 ring-2 ring-white flex items-center justify-center text-[9px] font-bold text-gray-500">
                  {nm[0]?.toUpperCase()}
                </div>
              );
            })}
            {assigned.length > 8 && (
              <div className="w-6 h-6 rounded-full bg-gray-100 ring-2 ring-white flex items-center justify-center text-[8px] font-bold text-gray-400">
                +{assigned.length - 8}
              </div>
            )}
          </div>
          <span className="text-[10px] text-gray-400 ml-1">{assigned.length} leden</span>
        </div>
      )}

      {/* Expanded: member picker */}
      {hasMemberPicker && expanded && onAssignedChange && (
        <div className="border-t border-gray-100 px-4 py-3 bg-gradient-to-b from-gray-50/80 to-white">
          <div className="mb-2.5">
            <p className="text-[11px] font-bold text-gray-800">Wie zit in deze ronde?</p>
            <p className="text-[10px] text-gray-500 mt-0.5 leading-relaxed">
              Tik op <span className="font-semibold text-emerald-700">Toevoegen</span> bij een lid, of gebruik onderaan &ldquo;Alle leden toevoegen&rdquo;.
            </p>
          </div>
          <p className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider mb-2">In deze ronde</p>
          <div className="space-y-1 max-h-[240px] overflow-y-auto">
            {/* Assigned members (reorderable) */}
            {assigned.map((mi, pos) => {
              const av = memberAvatars?.[mi];
              const nm = memberNames![mi] ?? `Lid ${mi + 1}`;
              return (
                <div key={`assigned-${mi}`} className="flex items-center gap-2 px-2 py-1.5 bg-white rounded-xl border border-gray-100 group/item">
                  <span className="text-[10px] text-gray-300 font-mono w-4 text-center">{pos + 1}</span>
                  {av ? (
                    <img src={av} alt={nm} className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-[10px] font-bold text-gray-500">
                      {nm[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="text-xs text-gray-800 font-medium flex-1 truncate">{nm}</span>
                  <div className="flex items-center gap-0.5 opacity-0 group-hover/item:opacity-100 transition-opacity">
                    <button
                      onClick={() => {
                        if (pos > 0) {
                          const next = [...assigned];
                          [next[pos - 1], next[pos]] = [next[pos], next[pos - 1]];
                          onAssignedChange(block.id, next);
                        }
                      }}
                      disabled={pos === 0}
                      className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-gray-600 disabled:opacity-20">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" /></svg>
                    </button>
                    <button
                      onClick={() => {
                        if (pos < assigned.length - 1) {
                          const next = [...assigned];
                          [next[pos], next[pos + 1]] = [next[pos + 1], next[pos]];
                          onAssignedChange(block.id, next);
                        }
                      }}
                      disabled={pos === assigned.length - 1}
                      className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-gray-600 disabled:opacity-20">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" /></svg>
                    </button>
                    <button
                      onClick={() => onAssignedChange(block.id, assigned.filter((_, i) => i !== pos))}
                      className="w-5 h-5 flex items-center justify-center rounded text-gray-300 hover:text-red-500">
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Unassigned members (click to add) */}
            {memberNames!.some((_, mi) => !assigned.includes(mi)) && (
              <p className="text-[9px] font-semibold text-emerald-700/80 uppercase tracking-wider mt-3 mb-1.5 pt-2 border-t border-emerald-100">
                Nog niet in deze ronde — tik om toe te voegen
              </p>
            )}
            {memberNames!.map((nm, mi) => {
              if (assigned.includes(mi)) return null;
              const av = memberAvatars?.[mi];
              return (
                <button
                  key={`unassigned-${mi}`}
                  type="button"
                  onClick={() => onAssignedChange(block.id, [...assigned, mi])}
                  className="flex items-center gap-3 w-full px-3 py-2 rounded-xl border-2 border-dashed border-emerald-200/90 bg-white text-left hover:border-emerald-400 hover:bg-emerald-50/50 transition-all group/add shadow-sm"
                >
                  {av ? (
                    <img src={av} alt={nm} className="w-8 h-8 rounded-full object-cover ring-2 ring-emerald-100" />
                  ) : (
                    <div className="w-8 h-8 rounded-full bg-emerald-50 ring-2 ring-emerald-100 flex items-center justify-center text-[11px] font-bold text-emerald-700">
                      {nm[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="text-xs text-gray-800 font-semibold flex-1 truncate">{nm}</span>
                  <span className="flex items-center gap-1.5 shrink-0">
                    <span className="text-[10px] font-bold text-emerald-700 uppercase tracking-wide hidden sm:inline">
                      Toevoegen
                    </span>
                    <span className="flex h-9 w-9 items-center justify-center rounded-full bg-emerald-500 text-white shadow-md group-hover/add:bg-emerald-600 group-hover/add:scale-105 transition-all">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.5} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
                      </svg>
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          {assigned.length > 0 && (
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
              <span className="text-[10px] text-gray-400">{assigned.length} van {memberNames!.length} leden</span>
              <button
                onClick={() => onAssignedChange(block.id, memberNames!.map((_, i) => i))}
                className="text-[10px] text-blue-500 hover:text-blue-700 font-medium transition-colors">
                Alle leden toevoegen
              </button>
            </div>
          )}
          {assigned.length === 0 && (
            <button
              onClick={() => onAssignedChange(block.id, memberNames!.map((_, i) => i))}
              className="mt-2 w-full py-1.5 text-[10px] text-blue-500 hover:text-blue-700 font-medium border border-blue-100 rounded-lg hover:bg-blue-50 transition-colors">
              Alle leden toevoegen
            </button>
          )}
        </div>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Template picker (shared between inline & config)
// ---------------------------------------------------------------------------

const TemplatePicker = memo(function TemplatePicker({
  entityId, effectiveMembers, dark,
  onApply, onSaveCustom, draft,
}: {
  entityId: string; effectiveMembers: number; dark?: boolean;
  onApply: (blocks: SessionBlock[]) => void;
  onSaveCustom?: () => void;
  draft: SessionBlock[];
}) {
  const [customTemplates, setCustomTemplates] = useState<CustomTemplate[]>([]);
  const [showCustom, setShowCustom] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);
  const loaded = useRef(false);

  useEffect(() => {
    if (loaded.current) return;
    loaded.current = true;
    loadCustomTemplates(entityId).then(setCustomTemplates);
  }, [entityId]);

  const handleSaveCustom = async () => {
    if (!saveName.trim()) return;
    await saveCustomTemplate(entityId, saveName.trim(), draft);
    const updated = await loadCustomTemplates(entityId);
    setCustomTemplates(updated);
    setSaveName('');
    setShowSaveInput(false);
  };

  const handleDeleteCustom = async (tplId: string) => {
    await deleteCustomTemplate(entityId, tplId);
    setCustomTemplates((prev) => prev.filter((t) => t.id !== tplId));
  };

  const btnCls = dark
    ? 'border-gray-500 text-gray-300 hover:border-gray-400 hover:bg-gray-600'
    : 'border-gray-200 text-gray-600 hover:border-gray-400 hover:bg-gray-50';
  const autoCls = dark
    ? 'border-emerald-600/50 text-emerald-400 hover:bg-emerald-900/30'
    : 'border-emerald-200 text-emerald-700 bg-emerald-50 hover:bg-emerald-100';

  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <p className={`text-[9px] font-semibold uppercase ${dark ? 'text-gray-400' : 'text-gray-500'}`}>Template</p>
        {customTemplates.length > 0 && (
          <button onClick={() => setShowCustom((s) => !s)}
            className={`text-[8px] ${dark ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}>
            {showCustom ? 'Standaard' : `Eigen (${customTemplates.length})`}
          </button>
        )}
      </div>

      {showCustom ? (
        <div className="space-y-1">
          {customTemplates.map((tpl) => (
            <div key={tpl.id} className="flex items-center gap-1">
              <button onClick={() => onApply(tpl.blocks)}
                className={`flex-1 h-6 text-[9px] font-medium rounded border ${btnCls} truncate px-2 text-left`}>
                {tpl.name}
              </button>
              <button onClick={() => handleDeleteCustom(tpl.id)}
                className="w-5 h-5 flex items-center justify-center text-gray-400 hover:text-red-400 shrink-0">
                <DeleteIcon size={9} weight="regular" />
              </button>
            </div>
          ))}
        </div>
      ) : (
        <>
          <div className="flex gap-1">
            {PRESET_TEMPLATES.map((t) => (
              <button key={t.label} onClick={() => onApply(generateTemplate(t.memberCount))}
                className={`flex-1 h-6 text-[9px] font-medium rounded border ${btnCls} transition-colors`}>
                {t.label}
              </button>
            ))}
          </div>
          <button onClick={() => onApply(generateTemplate(effectiveMembers))}
            className={`w-full h-6 text-[9px] font-medium rounded border ${autoCls} transition-colors`}>
            Auto ({effectiveMembers} leden, 60 min)
          </button>
        </>
      )}

      {/* Save as custom template */}
      {showSaveInput ? (
        <div className="flex gap-1">
          <input type="text" value={saveName} onChange={(e) => setSaveName(e.target.value)}
            placeholder="Naam template..."
            onKeyDown={(e) => { if (e.key === 'Enter') handleSaveCustom(); if (e.key === 'Escape') setShowSaveInput(false); }}
            autoFocus
            className={`flex-1 h-6 text-[9px] rounded border px-2 focus:outline-none ${dark ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-500' : 'border-gray-200 text-gray-900 placeholder-gray-400'}`} />
          <button onClick={handleSaveCustom}
            className="h-6 px-2 text-[9px] font-semibold rounded bg-emerald-600 text-white hover:bg-emerald-700">
            <CheckIcon size={9} weight="bold" />
          </button>
          <button onClick={() => setShowSaveInput(false)}
            className={`h-6 px-1.5 text-[9px] rounded ${dark ? 'text-gray-400 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`}>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>
      ) : (
        <button onClick={() => setShowSaveInput(true)}
          className={`w-full h-5 text-[8px] font-medium rounded ${dark ? 'text-gray-500 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'} transition-colors`}>
          Opslaan als eigen template
        </button>
      )}
    </div>
  );
});

// ---------------------------------------------------------------------------
// Inline settings panel (inside live meeting sidebar)
// ---------------------------------------------------------------------------

const InlineSettingsPanel = memo(function InlineSettingsPanel({
  entityId, blocks, onSave, onClose, memberCount = 1,
}: {
  entityId: string; blocks: SessionBlock[];
  onSave: (blocks: SessionBlock[]) => Promise<void>;
  onClose: () => void; memberCount?: number;
}) {
  const [draft, setDraft] = useState<SessionBlock[]>(() =>
    blocks.length > 0 ? [...blocks].sort((a, b) => a.order - b.order) : [...DEFAULT_BLOCKS],
  );
  const [saving, setSaving] = useState(false);
  const effectiveMembers = Math.max(1, memberCount);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try { await onSave(draft); onClose(); } finally { setSaving(false); }
  }, [draft, onSave, onClose]);

  const totalMinutes = draft.reduce(
    (sum, b) => sum + (b.perPerson && effectiveMembers > 1 ? b.durationMinutes * effectiveMembers : b.durationMinutes), 0,
  );

  return (
    <div className="bg-gray-700/80 backdrop-blur rounded-lg p-3 space-y-2 border border-gray-600">
      <div className="flex items-center justify-between">
        <span className="text-[10px] font-bold text-gray-300 uppercase tracking-wide">Instellingen</span>
        <button onClick={onClose} className="text-gray-400 hover:text-white transition-colors">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
        </button>
      </div>

      <TemplatePicker entityId={entityId} effectiveMembers={effectiveMembers} dark
        onApply={(b) => setDraft(b)} draft={draft} />

      <div className="space-y-0.5 max-h-[200px] overflow-y-auto">
        {draft.map((block, idx) => (
          <BlockEditorRow key={block.id} block={block} idx={idx} total={draft.length} effectiveMembers={effectiveMembers} dark
            onLabelChange={(id, v) => setDraft((p) => p.map((b) => b.id === id ? { ...b, label: v } : b))}
            onDurationChange={(id, v) => setDraft((p) => p.map((b) => b.id === id ? { ...b, durationMinutes: v } : b))}
            onTogglePP={(id) => setDraft((p) => p.map((b) => b.id === id ? { ...b, perPerson: !b.perPerson } : b))}
            onMoveUp={(i) => setDraft((p) => moveBlockUp(p, i))}
            onMoveDown={(i) => setDraft((p) => moveBlockDown(p, i))}
            onDelete={(id) => setDraft((p) => p.filter((b) => b.id !== id).map((b, i) => ({ ...b, order: i })))}
          />
        ))}
      </div>

      <div className="flex gap-1.5">
        <button onClick={() => setDraft((p) => [...p, { id: `b-${Date.now()}`, type: 'custom' as SessionBlockType, label: 'Nieuw', durationMinutes: 5, order: p.length }])}
          className="flex-1 h-7 text-[9px] font-medium rounded border border-gray-600 text-gray-400 hover:text-white hover:border-gray-500 flex items-center justify-center gap-1 transition-colors">
          <AddIcon size={10} weight="bold" />Blok
        </button>
        <button onClick={handleSave} disabled={saving}
          className="flex-1 h-7 text-[9px] font-semibold rounded bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-1 transition-colors">
          <CheckIcon size={10} weight="bold" />{saving ? '...' : `Opslaan (${totalMinutes}m)`}
        </button>
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Live Session View
// ---------------------------------------------------------------------------

const LiveSessionView = memo(function LiveSessionView({
  entityId, meetingId, blocks, onBlocksChange,
  memberCount = 1, memberNames = [], memberAvatars = [], isAdmin = false,
}: {
  entityId: string; meetingId: string; blocks: SessionBlock[];
  onBlocksChange?: (updated: SessionBlock[]) => void;
  memberCount?: number; memberNames?: string[];
  memberAvatars?: (string | null)[]; isAdmin?: boolean;
}) {
  const {
    currentBlock, elapsedSeconds, remainingSeconds, progressPercent,
    totalBlocks, flow, canGoNext, canGoPrev,
    start, pause, nextBlock, prevBlock, resetCurrentBlock,
  } = useSessionFlow(entityId, meetingId, blocks, { autoAdvance: !!isAdmin, canControl: !!isAdmin });

  const [currentPersonIdx, setCurrentPersonIdx] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const prevBlockIndex = useRef(flow.currentBlockIndex);
  if (prevBlockIndex.current !== flow.currentBlockIndex) {
    prevBlockIndex.current = flow.currentBlockIndex;
    setCurrentPersonIdx(0);
  }

  const effectiveMemberCount = Math.max(1, memberCount);
  const slotStart = currentBlock?.memberSlotStart ?? 0;
  const slotCount = currentBlock?.memberSlotCount ?? effectiveMemberCount;
  const isPerPerson = currentBlock?.perPerson && slotCount > 0;
  const globalPersonIdx = slotStart + currentPersonIdx;
  const currentPersonName = memberNames[globalPersonIdx] ?? `Persoon ${globalPersonIdx + 1}`;
  const currentPersonAvatar = memberAvatars[globalPersonIdx] ?? null;

  // Auto-advance
  const autoAdvanceFired = useRef(false);
  useEffect(() => {
    if (!isAdmin) { autoAdvanceFired.current = false; return; }
    if (!flow.isRunning || !currentBlock) { autoAdvanceFired.current = false; return; }
    const dur = currentBlock.durationMinutes * 60;
    if (flow.elapsedSeconds >= dur && !autoAdvanceFired.current) {
      autoAdvanceFired.current = true;
      const t = setTimeout(() => {
        if (isPerPerson && currentPersonIdx < slotCount - 1) {
          setCurrentPersonIdx((i) => i + 1);
          resetCurrentBlock();
        } else if (flow.currentBlockIndex < blocks.length - 1) {
          nextBlock();
        } else {
          pause();
        }
      }, 2000);
      return () => clearTimeout(t);
    }
    if (flow.elapsedSeconds < dur) autoAdvanceFired.current = false;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [flow.elapsedSeconds, flow.isRunning, currentBlock]);

  const nextPerson = useCallback(() => {
    if (!isPerPerson) return;
    autoAdvanceFired.current = false;
    if (currentPersonIdx < slotCount - 1) { setCurrentPersonIdx((i) => i + 1); resetCurrentBlock(); }
    else nextBlock();
  }, [isPerPerson, currentPersonIdx, slotCount, resetCurrentBlock, nextBlock]);

  const prevPerson = useCallback(() => {
    if (!isPerPerson) return;
    autoAdvanceFired.current = false;
    if (currentPersonIdx > 0) { setCurrentPersonIdx((i) => i - 1); resetCurrentBlock(); }
    else prevBlock();
  }, [isPerPerson, currentPersonIdx, resetCurrentBlock, prevBlock]);

  const sortedBlocks = useMemo(() => [...blocks].sort((a, b) => a.order - b.order), [blocks]);

  if (!currentBlock) return <div className="text-xs text-gray-400 text-center py-4">Geen blokken geconfigureerd.</div>;

  const isOverTime = elapsedSeconds > (currentBlock.durationMinutes * 60);
  const isAlmostDone = !isOverTime && remainingSeconds <= 3 && remainingSeconds > 0 && flow.isRunning;
  const dotColor = BLOCK_DOT_COLORS[currentBlock.type];

  return (
    <div className="space-y-3">

      {showSettings && isAdmin && onBlocksChange && (
        <InlineSettingsPanel entityId={entityId} blocks={blocks}
          onSave={async (nb) => { await onBlocksChange(nb); }} onClose={() => setShowSettings(false)} memberCount={memberCount} />
      )}

      {/* Active block label + step */}
      <div className="flex items-center justify-between px-0.5">
        <div className="flex items-center gap-2 min-w-0">
          <div className={`w-2 h-2 rounded-full shrink-0 ${dotColor}`} />
          <span className="text-sm font-semibold text-white truncate">{currentBlock.label}</span>
          {isPerPerson && (
            <span className="text-xs text-white/50 truncate max-w-[80px]">{currentPersonName}</span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[11px] text-white/30">{flow.currentBlockIndex + 1}/{totalBlocks}</span>
          {isAdmin && onBlocksChange && (
            <button onClick={() => setShowSettings((s) => !s)} title="Bewerken"
              className={`w-5 h-5 rounded flex items-center justify-center transition-colors ${showSettings ? 'text-white' : 'text-white/25 hover:text-white/60'}`}>
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <circle cx="12" cy="12" r="3" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Timer — large, centered */}
      <div className="text-center py-1">
        <div className={`text-[46px] font-black leading-none tabular-nums tracking-tight ${isOverTime ? 'text-red-400' : isAlmostDone ? 'text-amber-300 animate-pulse' : 'text-white'}`}>
          {isOverTime ? '+' : ''}{formatTime(isOverTime ? elapsedSeconds - currentBlock.durationMinutes * 60 : remainingSeconds)}
        </div>
        {/* Progress bar */}
        <div className="h-0.5 bg-white/10 rounded-full overflow-hidden mt-3 mx-2">
          <div className={`h-full rounded-full transition-all duration-1000 ${isOverTime ? 'bg-red-400' : isAlmostDone ? 'bg-amber-300' : 'bg-white/60'}`}
            style={{ width: `${progressPercent}%` }} />
        </div>
      </div>

      {/* Controls — alleen organisator/host (niet voor leden) */}
      <div className="flex items-center justify-center gap-3 min-h-[44px]">
        {isAdmin ? (
          <>
            <button type="button" onClick={isPerPerson ? prevPerson : (canGoPrev ? prevBlock : undefined)}
              disabled={isPerPerson ? (currentPersonIdx === 0 && !canGoPrev) : !canGoPrev}
              className="w-8 h-8 rounded-full flex items-center justify-center text-white/30 hover:text-white/80 disabled:opacity-20 transition-colors">
              <ChevronLeft size={16} strokeWidth={2.5} />
            </button>
            <button type="button" onClick={flow.isRunning ? pause : start}
              className="w-11 h-11 rounded-full bg-white flex items-center justify-center hover:bg-white/90 active:scale-95 transition-all shadow-lg shadow-black/30"
              title={flow.isRunning ? 'Pauzeren' : 'Start'}>
              {flow.isRunning
                ? <PauseIcon size={16} weight="fill" className="text-gray-900" />
                : <PlayIcon size={16} weight="fill" className="text-gray-900 ml-0.5" />}
            </button>
            <button type="button" onClick={isPerPerson ? nextPerson : (canGoNext ? nextBlock : undefined)}
              disabled={isPerPerson ? false : !canGoNext}
              className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-20 ${isOverTime ? 'text-emerald-400 animate-pulse' : 'text-white/30 hover:text-white/80'}`}>
              <ChevronRight size={16} strokeWidth={2.5} />
            </button>
          </>
        ) : (
          <p className="text-[10px] text-white/35 text-center px-3 leading-snug max-w-[200px]">
            Timer en tijdlijn worden bediend door de organisator
          </p>
        )}
      </div>

      {/* Block list — clean, minimal */}
      <div className="space-y-0 mt-1">
        {sortedBlocks.map((block, idx) => {
          const isActive = idx === flow.currentBlockIndex;
          const isDone = idx < flow.currentBlockIndex;
          const bpp = block.perPerson;
          const bStart = block.memberSlotStart ?? 0;
          const bCount = block.memberSlotCount ?? effectiveMemberCount;
          return (
            <div key={block.id} className={`flex items-center gap-2.5 px-1 py-1.5 rounded-lg transition-all`} style={isActive ? { background: 'rgba(255,255,255,0.07)' } : {}}>
              {isDone
                ? <span className="w-3 text-center text-[9px] text-emerald-400/80 shrink-0">✓</span>
                : <div className={`w-1.5 h-1.5 rounded-full shrink-0 mx-[3px] ${isActive ? BLOCK_DOT_COLORS[block.type] : 'bg-white/15'}`} />
              }
              <span className={`text-xs flex-1 truncate transition-all ${isActive ? 'font-semibold text-white' : isDone ? 'text-white/25 line-through decoration-white/20' : 'text-white/45'}`}>
                {block.label}
              </span>
              {bpp && isActive && (
                <div className="flex -space-x-1 shrink-0">
                  {Array.from({ length: Math.min(bCount, 4) }).map((_, mi) => {
                    const gi = bStart + mi;
                    const av = memberAvatars[gi] ?? null;
                    const nm = memberNames[gi] ?? `P${gi + 1}`;
                    const cur = mi === currentPersonIdx;
                    const done = mi < currentPersonIdx;
                    return (
                      <div key={gi} title={nm}
                        className={`w-5 h-5 rounded-full border border-gray-800 transition-all shrink-0 ${cur ? 'ring-1 ring-amber-400 scale-110 z-10' : done ? 'opacity-25' : 'opacity-60'}`}>
                        {av ? <img src={av} alt="" className="w-full h-full rounded-full object-cover" />
                          : <div className="w-full h-full rounded-full bg-gray-600 flex items-center justify-center text-[7px] font-bold text-gray-300">{nm.charAt(0)}</div>}
                      </div>
                    );
                  })}
                </div>
              )}
              <span className={`text-[10px] shrink-0 ${isActive ? 'text-white/50' : 'text-white/20'}`}>
                {bpp ? `${block.durationMinutes}m/p` : `${block.durationMinutes}m`}
              </span>
            </div>
          );
        })}
      </div>

      {/* Bottom progress pills */}
      <div className="flex gap-1 px-0.5 pt-1">
        {sortedBlocks.map((b, i) => (
          <div key={b.id} className={`flex-1 h-0.5 rounded-full transition-all ${i < flow.currentBlockIndex ? 'bg-emerald-400/70' : i === flow.currentBlockIndex ? 'bg-white/70' : 'bg-white/10'}`} />
        ))}
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Config View (outside meeting)
// ---------------------------------------------------------------------------

const ConfigView = memo(function ConfigView({
  entityId, entityType, userId, blocks, onSave, memberCount = 1,
}: {
  entityId: string; entityType: StructuredGrowthEntityType; userId: string;
  blocks: SessionBlock[]; onSave: (b: SessionBlock[]) => Promise<void>; memberCount?: number;
}) {
  const [draft, setDraft] = useState<SessionBlock[]>(() =>
    blocks.length > 0 ? [...blocks].sort((a, b) => a.order - b.order) : [...DEFAULT_BLOCKS],
  );
  const [saving, setSaving] = useState(false);
  const effectiveMembers = Math.max(1, memberCount);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try { await onSave(draft); } finally { setSaving(false); }
  }, [draft, onSave]);

  const totalMinutes = draft.reduce(
    (sum, b) => sum + (b.perPerson && effectiveMembers > 1 ? b.durationMinutes * effectiveMembers : b.durationMinutes), 0,
  );

  return (
    <div className="space-y-3">
      <TemplatePicker entityId={entityId} effectiveMembers={effectiveMembers}
        onApply={(b) => setDraft(b)} draft={draft} />

      <div className="flex items-center justify-between">
        <span className="text-xs text-gray-500">
          Totaal: <strong className="text-gray-900">{totalMinutes} min</strong>
          {effectiveMembers > 1 && <span className="ml-1 text-gray-400">({effectiveMembers} leden)</span>}
        </span>
        <button onClick={() => setDraft((p) => [...p, { id: `b-${Date.now()}`, type: 'custom' as SessionBlockType, label: 'Nieuw blok', durationMinutes: 10, order: p.length }])}
          className="h-7 px-2.5 text-xs text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center gap-1 transition-colors">
          <AddIcon size={12} weight="bold" /><span>Blok</span>
        </button>
      </div>

      <div className="space-y-1 max-h-[300px] overflow-y-auto">
        {draft.map((block, idx) => (
          <BlockEditorRow key={block.id} block={block} idx={idx} total={draft.length} effectiveMembers={effectiveMembers}
            onLabelChange={(id, v) => setDraft((p) => p.map((b) => b.id === id ? { ...b, label: v } : b))}
            onDurationChange={(id, v) => setDraft((p) => p.map((b) => b.id === id ? { ...b, durationMinutes: v } : b))}
            onTogglePP={(id) => setDraft((p) => p.map((b) => b.id === id ? { ...b, perPerson: !b.perPerson } : b))}
            onMoveUp={(i) => setDraft((p) => moveBlockUp(p, i))}
            onMoveDown={(i) => setDraft((p) => moveBlockDown(p, i))}
            onDelete={(id) => setDraft((p) => p.filter((b) => b.id !== id).map((b, i) => ({ ...b, order: i })))}
          />
        ))}
      </div>

      <button onClick={handleSave} disabled={saving}
        className="w-full h-8 bg-gray-900 text-white text-xs font-medium rounded-xl hover:bg-gray-800 disabled:opacity-50 transition-colors flex items-center justify-center gap-1.5">
        <CheckIcon size={12} weight="bold" />
        <span>{saving ? 'Opslaan...' : 'Opslaan'}</span>
      </button>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Compact live strip
// ---------------------------------------------------------------------------

const CompactLiveStrip = memo(function CompactLiveStrip({
  entityId, meetingId, blocks, memberCount = 1, memberNames = [], isAdmin = false,
}: {
  entityId: string; meetingId: string; blocks: SessionBlock[];
  memberCount?: number; memberNames?: string[];
  isAdmin?: boolean;
}) {
  const active = blocks.length > 0 ? blocks : DEFAULT_BLOCKS;
  const { currentBlock, remainingSeconds, progressPercent, flow, canGoNext, canGoPrev, start, pause, nextBlock, prevBlock, resetCurrentBlock } =
    useSessionFlow(entityId, meetingId, active, { autoAdvance: !!isAdmin, canControl: !!isAdmin });

  const [pi, setPi] = useState(0);
  const pr = useRef(flow.currentBlockIndex);
  if (pr.current !== flow.currentBlockIndex) { pr.current = flow.currentBlockIndex; setPi(0); }
  if (!currentBlock) return null;

  const em = Math.max(1, memberCount);
  const sc = currentBlock.memberSlotCount ?? em;
  const ss = currentBlock.memberSlotStart ?? 0;
  const pp = currentBlock.perPerson && sc > 0;
  const ot = remainingSeconds === 0 && flow.elapsedSeconds > 0;

  return (
    <div className="bg-white border-t border-gray-100">
      <div className="h-1 bg-gray-100"><div className={`h-full transition-all duration-1000 ${ot ? 'bg-red-400' : 'bg-emerald-500'}`} style={{ width: `${progressPercent}%` }} /></div>
      <div className="flex items-center gap-2 px-3 py-2">
        <div className={`w-2 h-2 rounded-full shrink-0 ${BLOCK_DOT_COLORS[currentBlock.type]}`} />
        <span className="text-xs font-medium text-gray-900 truncate">{currentBlock.label}</span>
        {pp && <span className="text-[10px] text-amber-600 font-medium truncate max-w-[80px]">{memberNames[ss + pi] ?? `P${ss + pi + 1}`}</span>}
        <span className={`font-mono text-xs font-bold tabular-nums w-12 text-right ${ot ? 'text-red-500' : 'text-gray-900'}`}>
          {ot ? '+' : ''}{formatTime(ot ? flow.elapsedSeconds - currentBlock.durationMinutes * 60 : remainingSeconds)}
        </span>
        <div className="flex items-center gap-0.5 shrink-0">
          {isAdmin ? (
            <>
              <button type="button" onClick={() => { if (pp && pi > 0) { setPi((i) => i - 1); resetCurrentBlock(); } else if (canGoPrev) prevBlock(); }}
                disabled={!pp && !canGoPrev} className="w-6 h-6 rounded flex items-center justify-center text-gray-400 hover:text-gray-700 disabled:opacity-20">
                <ChevronLeft size={12} strokeWidth={2} /></button>
              <button type="button" onClick={flow.isRunning ? pause : start}
                className="w-6 h-6 rounded-full bg-gray-900 text-white flex items-center justify-center hover:bg-gray-700">
                {flow.isRunning ? <PauseIcon size={11} weight="fill" /> : <PlayIcon size={11} weight="fill" />}</button>
              <button type="button" onClick={() => { if (pp && pi < sc - 1) { setPi((i) => i + 1); resetCurrentBlock(); } else nextBlock(); }}
                disabled={!pp && !canGoNext} className={`w-6 h-6 rounded flex items-center justify-center ${ot ? 'text-emerald-500 animate-pulse' : 'text-gray-400 hover:text-gray-700'} disabled:opacity-20`}>
                <ChevronRight size={12} strokeWidth={2} /></button>
            </>
          ) : (
            <span className="text-[9px] text-gray-400 px-1 max-w-[100px] leading-tight">Alleen organisator</span>
          )}
        </div>
      </div>
    </div>
  );
});

// ---------------------------------------------------------------------------
// Main export
// ---------------------------------------------------------------------------

const SessionStructureEngine: React.FC<SessionStructureEngineProps> = ({
  entityId, entityType, isAdmin, userId,
  liveMode = false, meetingId = 'default', compactLive = false,
  memberCount = 1, memberNames = [], memberAvatars = [],
}) => {
  const { settings, updateSettings } = useStructuredGrowth(entityId, entityType);
  const blocks = settings?.sessionStructure ?? [];

  const handleSaveBlocks = useCallback(
    async (nb: SessionBlock[]) => { await updateSettings({ sessionStructure: nb }, userId); },
    [updateSettings, userId],
  );

  if (compactLive) {
    return <CompactLiveStrip entityId={entityId} meetingId={meetingId}
      blocks={blocks.length > 0 ? blocks : DEFAULT_BLOCKS} memberCount={memberCount} memberNames={memberNames} isAdmin={isAdmin} />;
  }

  return (
    <div>
      {!liveMode && (
        <div className="flex items-center gap-2 mb-1">
          <ClockIcon size={14} weight="regular" className="text-gray-400" />
          <span className="text-xs font-semibold text-gray-200">Sessiestructuur</span>
        </div>
      )}

      {liveMode ? (
        <LiveSessionView entityId={entityId} meetingId={meetingId}
          blocks={blocks.length > 0 ? blocks : DEFAULT_BLOCKS}
          onBlocksChange={isAdmin ? handleSaveBlocks : undefined}
          memberCount={memberCount} memberNames={memberNames} memberAvatars={memberAvatars} isAdmin={isAdmin} />
      ) : isAdmin ? (
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-xs text-gray-400 mb-3">Kies een template of stel handmatig in. Gebruik /p voor blokken per persoon.</p>
          <ConfigView entityId={entityId} entityType={entityType} userId={userId}
            blocks={blocks} onSave={handleSaveBlocks} memberCount={memberCount} />
        </div>
      ) : (
        <div className="space-y-1">
          {(blocks.length > 0 ? blocks : DEFAULT_BLOCKS).sort((a, b) => a.order - b.order).map((block, idx) => (
            <div key={block.id} className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-xs ${BLOCK_CARD_COLORS[block.type]}`}>
              <span className="w-3 text-[10px] font-bold opacity-50">{idx + 1}</span>
              <span className="flex-1 font-medium">{block.label}</span>
              <span className="text-[10px] opacity-60">
                {block.perPerson && memberCount > 1 ? `${block.durationMinutes}m/p x ${block.memberSlotCount ?? memberCount}` : `${block.durationMinutes}m`}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default memo(SessionStructureEngine);
